import React, { useState, useEffect } from 'react'
import { ButtonElement, useAngularServices } from '@/react/components'
import closeIcon from '@/assets/icons/close-icon.svg'
import { css } from '@emotion/react'
import { useRef } from 'react'
import { CurrentUserType } from '@/react/types/documentation'
import EmailInput from './EmailInput'
import { ExternalAssignee } from '@/react/types/observations'
import { BinDeleteIcon } from '@/react/componentAssets/BinDeleteIcon'
import { useEmailManagement } from '@/react/hooks/internalExternalSection/useEmailManagement'
import { useExternalAssigneeAPI } from '@/react/hooks/internalExternalSection/useExternalAssigneeAPI'

interface AssignExternalModalProps {
  title: string
  handleClose: () => void
  answerId: number
  observationId?: number
  questionId?: number
  externalAssignee: ExternalAssignee[]
  fetchAnswers: () => void
}

const AssignExternalModal = ({
  title = '',
  handleClose,
  answerId,
  observationId,
  questionId,
  externalAssignee,
  fetchAnswers,
}: AssignExternalModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const { CurrentUser } = useAngularServices()
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color
  const [isButtonDisabled, setButtonDisabled] = useState(false)

  const {
    email,
    setEmail,
    error,
    setError,
    pendingEmails,
    handleEmailConfirm,
    handleRemovePendingEmail,
  } = useEmailManagement({
    existingAssignees: externalAssignee,
  })

  const { processMultipleEmails, updateAnswerStatus } = useExternalAssigneeAPI({
    answerId,
    observationId,
    questionId,
    externalAssignees: externalAssignee,
  })

  const handleOutsideClick = (e: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      handleClose()
    }
  }

  const handleEmailConfirmWrapper = (email: string) => {
    handleEmailConfirm(email)
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [])

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  const handleSubmit = async () => {
    setButtonDisabled(true)
    setError('')

    try {
      // Process pending emails if any
      if (pendingEmails.length > 0) {
        const result = await processMultipleEmails(pendingEmails)

        if (!result.success) {
          setError(result.error || 'Failed to process emails')
          setButtonDisabled(false)
          return
        }
      }

      // Check if there are any external assignees (existing + newly added)
      const hasExistingAssignees = externalAssignee.some(
        (assignee) => !assignee.deleted,
      )
      const hasAssignees = hasExistingAssignees || pendingEmails.length > 0

      if (hasAssignees) {
        await updateAnswerStatus()
      }

      // Refresh data and close modal
      fetchAnswers()
      handleClose()
    } catch (error: unknown) {
      console.error('Error in OK button handler:', error)
      setError('An error occurred while processing assignments')
      setButtonDisabled(false)
    }
  }

  return (
    <div
      css={assignExternalModalStyles}
      className="modal-content"
      ref={modalRef}
      onMouseOver={(e) => {
        e.stopPropagation()
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <img
        src={closeIcon}
        alt="cross-icon"
        className="close-icon"
        onClick={() => {
          handleClose()
        }}
      />
      <div className="modal-content">
        <p className="message-name">{title}</p>
        <div className="email-input-container">
          <EmailInput
            onConfirm={handleEmailConfirmWrapper}
            onChange={setEmail}
            value={email}
            isLoading={false}
          />
          {error && <p className="error-message">{error}</p>}
        </div>
        <div className="assigned-users">
          {pendingEmails.map((email, index) => (
            <div
              className="assigned-user pending-assignee"
              key={`pending-${index}`}
            >
              <p>{email}</p>
              <BinDeleteIcon
                color={variableColor}
                onClick={() => handleRemovePendingEmail(email)}
                width="17px"
                height="17px"
              />
            </div>
          ))}
        </div>
        <ButtonElement
          text="OK"
          buttonType="submit"
          functionToTrigger={() => {
            void handleSubmit()
          }}
          disabled={isButtonDisabled || pendingEmails.length === 0}
        />
      </div>
    </div>
  )
}

const assignExternalModalStyles = css({
  position: 'absolute',
  backgroundColor: 'white',
  boxShadow: '0px 7px 29px 0px rgba(0, 0, 0, 0.18)',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 10000,
  isolation: 'isolate',
  '.modal-content': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px',
  },
  '.close-icon': {
    position: 'absolute',
    top: 10,
    right: 10,
    width: '10px',
    height: '10px',
    cursor: 'pointer',
    zIndex: 10,
  },
  '.message-name': {
    textAlign: 'center',
    fontSize: '14px',
    color: '#656565',
    fontWeight: 700,
    margin: 0,
    marginBottom: '24px',
  },
  '.email-input-container': {
    marginBottom: '16px',
  },
  '.assigned-users': {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
    marginBottom: '24px',
    width: '100%',
    maxWidth: '430px',
    overflowX: 'auto',
  },
  '.assigned-user': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    border: '1px solid #E5E6E7',
    backgroundColor: '#FAFAFA',
    padding: '8px',

    p: {
      color: '#000',
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '0.12px',
      margin: 0,
    },

    img: {
      cursor: 'pointer',
    },
  },
  '.error-message': {
    color: 'red',
    fontSize: '12px',
    fontWeight: 600,
    margin: 0,
    paddingTop: '8px',
  },
})

export default AssignExternalModal
